/**
 * Create / Edit Budget Modal — rolling plan; Active / Closed + end date when closed
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, Plus, Trash2, Loader2 } from 'lucide-react';
import { useCreateBudget, useUpdateBudget } from '../../hooks/useBudgetOverview';
import { CategoryFilterDropdown } from '../../components/transactions/CategoryFilterDropdown';
import { CategoryType } from '../../types/category.types';
import { DEFAULT_ALERT_THRESHOLDS } from '../../utils/budget-calculations';
import type { Budget, BudgetPeriodType, BudgetStatus, CreateBudgetInput } from '../../types/budget.types';
import { format } from 'date-fns';

interface CreateBudgetModalProps {
  budget?: Budget | null;
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

interface CategoryLimitRow {
  categoryId: string;
  amount: string;
}

function mapLegacyStatus(status?: string): BudgetStatus {
  if (status === 'closed' || status === 'paused' || status === 'completed' || status === 'carryover') {
    return 'closed';
  }
  return 'active';
}

export function CreateBudgetModal({
  budget,
  onClose,
  onDirtyChange,
}: CreateBudgetModalProps): React.ReactElement {
  const isEdit = !!budget;
  const closeRef = useRef<HTMLButtonElement>(null);
  const createBudget = useCreateBudget();
  const updateBudget = useUpdateBudget();

  const [name, setName] = useState(budget?.name ?? '');
  const [periodType, setPeriodType] = useState<BudgetPeriodType>(budget?.periodType ?? 'monthly');
  const [status, setStatus] = useState<BudgetStatus>(mapLegacyStatus(budget?.status));
  const [endDate, setEndDate] = useState(
    budget?.endDate ?? format(new Date(), 'yyyy-MM-dd')
  );
  const [warning, setWarning] = useState(String(budget?.alertThresholds.warning ?? DEFAULT_ALERT_THRESHOLDS.warning));
  const [critical, setCritical] = useState(String(budget?.alertThresholds.critical ?? DEFAULT_ALERT_THRESHOLDS.critical));
  const [limit, setLimit] = useState(String(budget?.alertThresholds.limit ?? DEFAULT_ALERT_THRESHOLDS.limit));
  const [categoryLimits, setCategoryLimits] = useState<CategoryLimitRow[]>(
    budget?.categoryLimits?.map((l) => ({
      categoryId: l.categoryId,
      amount: String(l.planAmount ?? l.amount),
    })) ?? [{ categoryId: '', amount: '' }]
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onDirtyChange?.(true);
  }, [name, periodType, status, endDate, warning, critical, limit, categoryLimits, onDirtyChange]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const addCategoryRow = () => {
    setCategoryLimits((prev) => [...prev, { categoryId: '', amount: '' }]);
  };

  const removeCategoryRow = (index: number) => {
    setCategoryLimits((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCategoryRow = (index: number, field: keyof CategoryLimitRow, value: string) => {
    setCategoryLimits((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleStatusChange = (next: BudgetStatus) => {
    setStatus(next);
    if (next === 'closed' && !endDate) {
      setEndDate(format(new Date(), 'yyyy-MM-dd'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const limits = categoryLimits
      .filter((l) => l.categoryId && l.amount)
      .map((l) => ({ categoryId: l.categoryId, amount: parseFloat(l.amount) }));

    if (limits.length === 0) {
      setError('Add at least one category limit.');
      return;
    }

    if (status === 'closed' && !endDate) {
      setError('End date is required when status is closed.');
      return;
    }

    const input: CreateBudgetInput = {
      name: name.trim(),
      periodType,
      status,
      endDate: status === 'closed' ? endDate : null,
      alertThresholds: {
        warning: parseInt(warning, 10),
        critical: parseInt(critical, 10),
        limit: parseInt(limit, 10),
      },
      categoryLimits: limits,
    };

    try {
      if (isEdit && budget) {
        await updateBudget.mutateAsync({ id: budget.id, input });
      } else {
        await createBudget.mutateAsync(input);
      }
      onDirtyChange?.(false);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget');
    }
  };

  const isPending = createBudget.isPending || updateBudget.isPending;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="create-budget-title">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 id="create-budget-title" className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {isEdit ? 'Edit Budget' : 'Create Budget'}
            </h2>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="budget-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Budget Name
              </label>
              <input
                id="budget-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500"
                placeholder="e.g. Everyday Spending"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Set once — limits apply to every {periodType === 'weekly' ? 'week' : periodType === 'yearly' ? 'year' : 'month'}. Use the overview navigator to look back.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="period-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Period Type
                </label>
                <select
                  id="period-type"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value as BudgetPeriodType)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label htmlFor="budget-status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  id="budget-status"
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value as BudgetStatus)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            {status === 'closed' && (
              <div>
                <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date
                </label>
                <input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  className="w-full sm:max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Date this plan was closed. Closed plans are hidden from the overview.
                </p>
              </div>
            )}

            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Alert Thresholds (%)</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label htmlFor="threshold-warning" className="text-xs text-gray-500 dark:text-gray-400">Warning</label>
                  <input
                    id="threshold-warning"
                    type="number"
                    min={1}
                    max={100}
                    value={warning}
                    onChange={(e) => setWarning(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label htmlFor="threshold-critical" className="text-xs text-gray-500 dark:text-gray-400">Critical</label>
                  <input
                    id="threshold-critical"
                    type="number"
                    min={1}
                    max={100}
                    value={critical}
                    onChange={(e) => setCritical(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label htmlFor="threshold-limit" className="text-xs text-gray-500 dark:text-gray-400">Limit</label>
                  <input
                    id="threshold-limit"
                    type="number"
                    min={1}
                    max={100}
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Category Limits</h3>
                <button
                  type="button"
                  onClick={addCategoryRow}
                  className="flex items-center gap-1 text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700"
                >
                  <Plus className="h-4 w-4" /> Add Category
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Amounts are per {periodType === 'weekly' ? 'week' : periodType === 'yearly' ? 'year' : 'month'}.
              </p>
              <div className="space-y-2">
                {categoryLimits.map((row, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <CategoryFilterDropdown
                      mode="select"
                      value={row.categoryId ? [row.categoryId] : []}
                      onChange={(ids) => updateCategoryRow(index, 'categoryId', ids[0] ?? '')}
                      types={[CategoryType.EXPENSE]}
                      hideExcludedFromSpending
                      excludeIds={categoryLimits
                        .map((r, i) => (i !== index && r.categoryId ? r.categoryId : null))
                        .filter((id): id is string => !!id)}
                      emptyLabel="Select category"
                      className="flex-1 min-w-0"
                      aria-label={`Category ${index + 1}`}
                    />
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={row.amount}
                      onChange={(e) => updateCategoryRow(index, 'amount', e.target.value)}
                      placeholder="Amount"
                      className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      aria-label={`Amount for category ${index + 1}`}
                    />
                    {categoryLimits.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCategoryRow(index)}
                        className="p-2 text-red-500 hover:text-red-700"
                        aria-label="Remove category"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEdit ? 'Save Changes' : 'Create Budget'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
